/**
 * Release notes — curated, newest first.
 *
 * `audience` controls who sees an entry:
 *   'everyone' — shown to all users (learners + admins)
 *   'admin'    — shown only to administrators
 *
 * Keep entries short and outcome-focused; this is a product changelog, not a
 * commit log. Add new releases to the TOP of the array.
 */
export type ReleaseAudience = 'everyone' | 'admin';

export interface ReleaseNote {
  id: string;
  /** ISO date, e.g. '2026-07-24'. */
  date: string;
  title: string;
  /** One-line summary shown under the title. */
  summary: string;
  /** Bullet highlights. */
  highlights: string[];
  audience: ReleaseAudience;
  /** Short tag rendered as a chip, e.g. 'New', 'Improved', 'Fix'. */
  tag?: 'New' | 'Improved' | 'Fix';
}

export const RELEASE_NOTES: ReleaseNote[] = [
  {
    id: '2026-07-24-agent-builder',
    date: '2026-07-24',
    title: 'Guided AI agent builder',
    summary: 'Describe the simulation you want in plain language and let the assistant draft it for you.',
    highlights: [
      'Chat with a design assistant that interviews you one question at a time, then generates a complete, editable agent draft.',
      'Automatically prepares the five instruction sections, rep preparation and — for coaches — a post-session report template.',
      'Your work is saved. Close the assistant and pick up where you left off from Design History.',
      'Review and edit everything before anything is applied — nothing is published automatically.',
    ],
    audience: 'admin',
    tag: 'New',
  },
  {
    id: '2026-07-24-output-templates',
    date: '2026-07-24',
    title: 'AI-generated coach report templates',
    summary: 'Create a post-session report template with AI, or keep using your approved ones.',
    highlights: [
      'The assistant can generate a tailored output document for a coach and attach it automatically.',
      'Existing approved templates (e.g. Sales Call Planning Sheet) remain available for regulated or branded formats.',
      'The generated template is saved as a reusable template only when the agent is created — no clutter from abandoned drafts.',
    ],
    audience: 'admin',
    tag: 'New',
  },
  {
    id: '2026-07-23-enterprise-hierarchy',
    date: '2026-07-23',
    title: 'Enterprise organisation & role dashboards',
    summary: 'Structure your organisation into branches and units, with dashboards scoped to each role.',
    highlights: [
      'Company → branch → unit structure with scoped administrators and managers.',
      'Role-based dashboards: company administrators see everything; branch and unit managers see only their own people and results.',
      '7, 30 and 90-day reporting windows across sessions, scores and training time.',
      'Reports preserve the branch and unit a learner belonged to at the time, so moving someone later does not rewrite history.',
    ],
    audience: 'admin',
    tag: 'New',
  },
  {
    id: '2026-07-22-agent-quality',
    date: '2026-07-22',
    title: 'Five-section agent authoring',
    summary: 'Create or deliberately convert agents using a clearer, structured instruction format.',
    highlights: [
      'Organise agent instructions into Personality, Environment, Tone, Goal and Guardrails.',
      'Existing agents keep their current behaviour until an administrator explicitly converts and reviews them.',
      'Persona names and opening messages are separated from tile titles so agents introduce themselves correctly.',
      'Each section includes practical authoring guidance and examples.',
    ],
    audience: 'admin',
    tag: 'Improved',
  },
  {
    id: '2026-07-22-categories',
    date: '2026-07-22',
    title: 'Find simulations faster with Categories',
    summary: 'Browse and filter simulations by category.',
    highlights: [
      'Every simulation can be organised under a category (for example Sales, Clinical or Onboarding).',
      'Filter your simulations by category to get to the right one quickly.',
    ],
    audience: 'everyone',
    tag: 'New',
  },
  {
    id: '2026-07-21-branding',
    date: '2026-07-21',
    title: 'Your organisation’s branding, end to end',
    summary: 'The platform, emails and downloadable reports now carry your organisation’s look.',
    highlights: [
      'Your logo and colours appear across the app, session-completion emails and generated documents.',
      'Downloadable coaching reports (Word and PDF) are branded consistently.',
    ],
    audience: 'everyone',
    tag: 'Improved',
  },
  {
    id: '2026-07-20-reliability',
    date: '2026-07-20',
    title: 'More reliable video sessions & recordings',
    summary: 'Fewer interruptions, and session recordings save reliably.',
    highlights: [
      'Video sessions wait longer for slower avatar connections before starting.',
      'Recordings are fully finalised before a session ends, so replays are no longer lost.',
      'Completed sessions appear in My Sessions immediately.',
    ],
    audience: 'everyone',
    tag: 'Fix',
  },
];

export function visibleReleaseNotes(isAdmin: boolean): ReleaseNote[] {
  return RELEASE_NOTES.filter((note) => isAdmin || note.audience === 'everyone');
}
